import { Check } from "lucide-react";
import { motion } from "framer-motion";

const highlights = [
  "Product Lifecycle Management",
  "Cross-functional Leadership",
  "Data-Driven Decision Making",
  "Strategic Planning",
  "User Research & Analysis",
  "Agile Methodologies",
];

const About = () => {
  return (
    <section id="about" className="bg-white border-b border-slate-200 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* THE GRID LAYOUT */}
        <div className="grid lg:grid-cols-2">
          
          {/* --- COLUMN 1: The Image --- */}
          {/* Reliable curtain reveal with scale and slide-up curtain */}
          <div className="relative h-full min-h-[600px] group overflow-hidden bg-slate-100">
              
              {/* 1. The Image (Scale Effect) */}
              <motion.img
                src="https://static.wixstatic.com/media/cb3ba5_c16afe183eab484ca668254d0a669024~mv2.jpg/v1/fill/w_800,h_1000,al_c,q_90/profile.jpg"
                alt="Anant Mishra"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                initial={{ scale: 1.2 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: false }}
                transition={{ duration: 1.4 }}
              />

              {/* 2. The Curtain (Solid White Block that slides UP) */}
              <motion.div
                initial={{ height: "100%" }}
                whileInView={{ height: "0%" }}
                viewport={{ once: false, amount: 0.1 }} // Triggers earlier to prevent white-out
                transition={{ duration: 0.8, ease: "easeInOut" }} // Faster, simpler ease
                className="absolute bottom-0 left-0 w-full bg-white z-20"
              />
          </div>

          {/* --- COLUMN 2: The Content --- */}
          <div className="flex flex-col">
            
            {/* Row 1: Header */}
            <div className="p-12 md:p-16 pb-8 relative">
              
              {/* ANIMATED BORDER: Bottom Line */}
              <motion.div 
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: false }}
                transition={{ duration: 1.5, ease: "easeInOut", delay: 0.2 }}
                className="absolute bottom-0 left-0 h-px bg-slate-200"
              />

              {/* Label Reveal */}
              <div className="flex items-center gap-4 mb-8 overflow-hidden">
                <motion.span 
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: false }}
                  className="h-px w-8 bg-blue-600"
                />
                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs block">
                  About Me
                </span>
              </div>

              {/* Headline */}
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-[1.1] tracking-tight">
                Turning Vision into<br />
                <motion.span 
                   initial={{ opacity: 0, x: -20 }}
                   whileInView={{ opacity: 1, x: 0 }}
                   viewport={{ once: false }}
                   transition={{ delay: 0.2 }}
                   className="text-blue-600 inline-block"
                >
                  Products that Impact.
                </motion.span>
              </h2>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: 0.3 }}
                className="space-y-6 text-lg text-slate-500 leading-relaxed"
              >
                <p>
                  As a Product Manager and Strategist, I'm dedicated to building products that deliver meaningful, impactful solutions for end users. With a strong foundation in computer science, I serve as a bridge between technical teams and management.
                </p>
                <p>
                  I bring clarity to the "Why" behind crucial product questions, energizing teams toward shared visions and measurable goals.
                </p>
              </motion.div>
            </div>

            {/* Row 2: The Stats Grid */}
            <div className="grid grid-cols-2 border-t border-slate-200 relative">
                 {/* ANIMATED BORDER: Middle Vertical Line */}
                <motion.div 
                   initial={{ height: 0 }}
                   whileInView={{ height: "100%" }}
                   viewport={{ once: false }}
                   transition={{ duration: 1, delay: 0.5 }}
                   className="absolute left-1/2 top-0 w-px bg-slate-200 h-full transform -translate-x-1/2"
                />

                {/* Stat 1 */}
                <div className="p-10 hover:bg-slate-50 transition-colors group">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="text-5xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">3+</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Years Experience</div>
                    </motion.div>
                </div>
                {/* Stat 2 */}
                <div className="p-10 hover:bg-slate-50 transition-colors group">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        transition={{ delay: 0.5 }}
                    >
                        <div className="text-5xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">15+</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Projects Shipped</div>
                    </motion.div>
                </div>
            </div>

            {/* Row 3: The Expertise List */}
            <div className="p-12 md:p-16 border-t border-slate-200 bg-slate-50/50">
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 mb-8">Core Competencies</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {highlights.map((item, index) => (
                    <motion.div 
                      key={item} 
                      initial={{ opacity: 0, x: 10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false }}
                      transition={{ delay: 0.6 + (index * 0.05) }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-slate-600 text-sm font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default About;